import { useMemo, useState } from 'react';

import { formatMetricValue, formatMetricValueCompact, type MetricFormat } from '@/lib/metric-format';

import { ChartFrame } from './chart-frame';
import { chartColor, shortenLabel, toFinite } from './chart-utils';

/** Radio y grosor del anillo en el sistema de coordenadas del SVG. */
const SIZE = 200;
const RADIUS = 72;
const STROKE = 26;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface DonutChartProps {
  title?: string;
  labels: (string | number)[];
  /** Sólo se representa la primera serie (distribución). */
  series: { label: string; data: (number | null)[] }[];
  format?: MetricFormat;
}

/**
 * Gráfico circular (anillo) con leyenda y porcentajes.
 *
 * Se construye con trazos de SVG (`stroke-dasharray`), sin librerías externas.
 * La primera serie define la distribución; permitiendo huecos (`null`).
 */
export function DonutChart({ title, labels, series, format = 'number' }: DonutChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  const seriesData = series[0];

  const slices = useMemo(() => {
    const values = labels.map((_, index) => Math.max(toFinite(seriesData?.data[index]) ?? 0, 0));
    const total = values.reduce((sum, value) => sum + value, 0);

    const items: {
      label: string;
      value: number;
      fraction: number;
      color: string;
      dashArray: string;
      dashOffset: number;
    }[] = [];

    let accumulated = 0;

    for (let index = 0; index < values.length; index += 1) {
      const value = values[index] ?? 0;
      const fraction = total > 0 ? value / total : 0;
      const length = fraction * CIRCUMFERENCE;

      items.push({
        label: String(labels[index] ?? ''),
        value,
        fraction,
        color: chartColor(index),
        dashArray: `${length} ${CIRCUMFERENCE - length}`,
        dashOffset: -accumulated,
      });

      accumulated += length;
    }

    return { total, items };
  }, [labels, seriesData]);

  if (slices.total <= 0) {
    return (
      <ChartFrame title={title}>
        <p className="py-8 text-center text-xs text-ink-500">Sin datos para graficar en este período.</p>
      </ChartFrame>
    );
  }

  return (
    <ChartFrame title={title}>
      {/* Dona y leyenda **apiladas**: en una tarjeta estrecha (una columna del
          dashboard) la leyenda en fila deja el nombre del método recortado, y en
          una tarjeta ancha la leyenda a todo lo ancho se lee igual de bien. */}
      <div className="flex flex-col items-center gap-3">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label={title ?? 'Distribución'}
          className="size-32 shrink-0"
        >
          <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
            {slices.items.map((slice, index) => {
              if (slice.value <= 0) return null;

              const isHovered = hover === index;

              return (
                <circle
                  key={slice.label}
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  stroke={slice.color}
                  strokeWidth={isHovered ? STROKE + 6 : STROKE}
                  strokeDasharray={slice.dashArray}
                  strokeDashoffset={slice.dashOffset}
                  opacity={hover === null || isHovered ? 1 : 0.55}
                  className="cursor-pointer transition-all duration-200"
                  onMouseEnter={() => setHover(index)}
                  onMouseLeave={() => setHover(null)}
                />
              );
            })}
          </g>

          <text
            x={SIZE / 2}
            y={SIZE / 2 - 4}
            textAnchor="middle"
            className="fill-ink-400 text-[13px]"
          >
            {hover !== null ? shortenLabel(slices.items[hover]?.label, 12) : 'Total'}
          </text>
          <text
            x={SIZE / 2}
            y={SIZE / 2 + 16}
            textAnchor="middle"
            className="fill-ink-900 text-[16px] font-semibold"
          >
            {formatMetricValueCompact(
              hover !== null ? (slices.items[hover]?.value ?? 0) : slices.total,
              format,
            )}
          </text>
        </svg>

        {/* `min-w-0`: la leyenda nunca debe forzar el ancho del contenedor. */}
        <ul className="w-full min-w-0 space-y-1.5">
          {slices.items.map((slice, index) => (
            <li
              key={slice.label}
              onMouseEnter={() => setHover(index)}
              onMouseLeave={() => setHover(null)}
              className="flex items-center justify-between gap-2 rounded-lg px-1.5 py-1 text-[11px] transition-colors hover:bg-ink-50"
            >
              <span className="flex min-w-0 items-center gap-1.5 text-ink-600">
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: slice.color }}
                />
                <span className="truncate" title={slice.label}>
                  {slice.label}
                </span>
              </span>

              <span className="flex shrink-0 items-center gap-2">
                <span className="font-mono font-semibold text-ink-800">
                  {formatMetricValue(slice.value, format)}
                </span>
                <span className="w-9 text-right font-mono text-ink-400">
                  {(slice.fraction * 100).toFixed(1)}%
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </ChartFrame>
  );
}
